"use client";

import { useUser } from "@clerk/nextjs";
import { useCallback, useEffect, useState } from "react";
import { sendEvent } from "@/features/analytics/gtm/event";
import { getLoginStrategy } from "@/features/analytics/gtm/login-strategy";
import { useFoodangoUser } from "@/features/analytics/hooks/use-foodango-user";
import { useFuidCookie } from "@/features/analytics/hooks/use-fuid-cookie";
import type {
  EventUserDataValue,
  UserPlatformType,
} from "@/features/analytics/types/event.type";
import { getPlatformFromUserAgent } from "@/lib/helpers/get-user-platform";

export const PageViewHandler = () => {
  const { user, isLoaded } = useUser(); // Clerk user
  const strategy = user?.primaryEmailAddress?.verification.strategy;
  const { data: foodangoUserQuery, isPending } = useFoodangoUser({
    email: user?.primaryEmailAddress?.emailAddress,
  }); // Foodango user

  const [platform, setPlatform] = useState<UserPlatformType>(null);

  // Manage fuid cookie
  useFuidCookie();

  const sendUserDataEvent = useCallback((extendedData: EventUserDataValue) => {
    sendEvent({
      event: "user_data",
      value: extendedData,
    });
  }, []);

  const prepareEventDataForLoggedInUser = useCallback(() => {
    const hasEmail = user?.primaryEmailAddress?.emailAddress;
    if (hasEmail && isPending) return null; // Foodango user data is loading

    const eventData: Partial<EventUserDataValue> = {};

    if (foodangoUserQuery?.id) {
      eventData.user_id = foodangoUserQuery.id;
    }
    if (strategy) {
      eventData.method = getLoginStrategy(strategy);
    }

    return eventData;
  }, [user, isPending, foodangoUserQuery, strategy]);

  // For loading the page via WebView in a mobile app, the code is loaded asynchronously
  // That's why we need to use this trick to wait for the navigator to become available
  // We try 20 times over 2 seconds
  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 20;
    const interval = setInterval(() => {
      if (
        typeof window !== "undefined" &&
        typeof window.navigator !== "undefined"
      ) {
        const userAgent = window.navigator.userAgent;
        const detectedPlatform = getPlatformFromUserAgent(userAgent);
        setPlatform(detectedPlatform);
        clearInterval(interval);
      } else if (attempts >= maxAttempts) {
        setPlatform("web");
        clearInterval(interval);
      }
      attempts++;
    }, 100);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Wait for platform and Clerk to be loaded
    if (!platform) return;
    if (!isLoaded) return;

    const eventData: EventUserDataValue = {
      page: window.location.pathname,
      stream: platform,
    };

    if (user) {
      const userData = prepareEventDataForLoggedInUser();
      if (userData === null) return; // Still loading user data

      Object.assign(eventData, userData);
    }

    sendUserDataEvent(eventData);
  }, [
    platform,
    isLoaded,
    user,
    prepareEventDataForLoggedInUser,
    sendUserDataEvent,
  ]);

  return null;
};
